/* eslint-disable no-undef */
import { useState } from 'react';
import { Settings } from 'luxon';
import i18next from 'i18next';
import HttpApi from 'i18next-http-backend';
import keyPlugin from './keyPlugin';
import localeToUpperCase from './localeToUppercase';

/*
  Some of the language codes that we use do not match the file names.
  This function will take the language code and globals and determine the right
  language code to be used for looking up the file.
*/
export const getFileNameForLanguage = language => {
  // This is the only language where the file name is
  // not follow the format of languageCode_LOCALE
  if (language === 'zh-CN') return 'zh';

  return language.replace('-', '_');
};

/*
  This will dynamically load a translation file for a specific language and
  add the file as a resource bundle to the current i18next instance under
  the namespace 'translation'.
*/
const addTranslationResource = async (lng, ns) => {
  const fileLng = getFileNameForLanguage(lng);
  try {
    const translations = await import(
      `../../../../../locale/${fileLng}/LC_MESSAGES/translation.json`
    );
    i18next.addResourceBundle(lng, ns, translations.default);
  } catch (e) {
    console.log(`Unable to load namespace ${ns} for ${lng}`);
  }
};

/*
This loads the library that we are using for js l10n.
It is here because the json files need to be loaded
before anything else is so the translations render correctly.

TECHDEBT: To help with the load, we do not make a call when the language
  is English since we currently do not use any assets in the English
  translation.json file. If this ever were to change, we would need to
  update the load path to not return false when the language is en, as
  well as update the logic that determines whether to load the local file.
*/

export const loadi18next = async () => {
  const defaultLanguageCode = Globals?.language_code || 'en';
  const languageCDN = Globals?.language_cdn_url;

  await i18next
    .use(HttpApi)
    .use(keyPlugin)
    .init({
      lng: defaultLanguageCode,
      nsSeparator: false,
      keySeparator: '.',
      ns: ['translation'],
      // The first namespace to check for translations
      defaultNS: 'translation',
      // The last namespace to check for translaitons
      fallbackNS: 'translation',
      // 'currentOnly' keeps i18next from trying to get both the base language and
      // language with locale when loading resources (i.e. will only load pt-br
      // instead of pt and pt-BR).
      load: 'currentOnly',
      // TECHDEBT: Once all instances of i18next.t outside of the translate function
      // are removed, the postProcess argument can be removed as well as use(keyPlugin)
      postProcess: ['keyViewPlugin'],
      // Having this be false removes prevents the loading of any fallback language.
      // Since we handle fallback logic in the translate function, there is no need
      // to load fallback values.
      fallbackLng: false,
      backend: {
        /*
          This is currently accessing the CDN where all of the translation.json
          files are stored.
        */
        loadPath: (lng, ns) => {
          if (defaultLanguageCode === 'en' || !languageCDN) return false;

          const fileLanguage = getFileNameForLanguage(lng[0]);
          return `${languageCDN}${fileLanguage}/${ns}.json`;
        },
      },
    });

  // If language_cdn_url is not defined in Globals, assume this is being run in local dev
  // and try to import the translation file directly.
  if (defaultLanguageCode !== 'en' && !languageCDN) {
    // i18next is VERY picky about the capitalization of the locale when
    // looking up if a resource exists. The locale must be in uppercase
    // but the language is in lowercase.
    await addTranslationResource(localeToUpperCase(i18next.language), 'translation');
  }

  return defaultLanguageCode;
};

export const useLanguageLoader = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  loadi18next().then(defaultLang => {
    setIsLoaded(true);
    Settings.defaultLocale = defaultLang;
  });
  if (!isLoaded) return false;
  return true;
}
